import { TFunction } from 'i18next';

import { Distribution, Granularity, ScoringMetric, deleteSavedScore, listSavedScores, putSavedScore } from './MetricsAPI';
import { FormulaVariable } from './FormulaEditor';
import { fixedCategory, inkarCategory, poiCategory, zensusCategory } from './categories';
import { POI_TYPES, poiGroupLabel, poiTypeLabel } from './poiTypes';

/**
 * The scores of the customer, kept on the server (`/metrics/scores`): every user of the customer sees
 * the same ones, on every machine.
 *
 * Scores used to live in this browser's localStorage. The first time the list is read from the
 * server, whatever is still stored here is uploaded under its own id and then removed — a score
 * written before the move is not lost, and is not uploaded twice.
 */

const LEGACY_STORAGE_KEY = 'tri.heatmap.scores';

export interface Score {
    id: string;
    name: string;
    description: string;
    formula: string;
    granularity: Granularity;
    distribution: Distribution;
}

const legacyScores = (): Score[] => {
    try {
        const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed)
            ? parsed
                  .filter((entry: any) => entry?.id && entry?.name && entry?.formula)
                  .map((entry: any) => ({ granularity: 'cell', distribution: 'buildings', description: '', ...entry }))
            : [];
    } catch {
        return [];
    }
};

const byName = (scores: Score[]) => [...scores].sort((first, second) => first.name.localeCompare(second.name, 'de'));

export const loadScores = async (): Promise<Score[]> => {
    const stored = await listSavedScores<Score>();
    const local = legacyScores().filter((entry) => !stored.some((score) => score.id === entry.id));
    if (local.length > 0) {
        const uploaded = await Promise.all(local.map((score) => putSavedScore(score).catch(() => null)));
        if (uploaded.every(Boolean)) {
            try {
                localStorage.removeItem(LEGACY_STORAGE_KEY);
            } catch {
                // A browser that refuses storage access has nothing left to clean up either.
            }
        }
        return byName([...stored, ...(uploaded.filter(Boolean) as Score[])]);
    }
    return byName(stored);
};

/** Insert or replace by id and return the new list. */
export const saveScore = async (scores: Score[], score: Score): Promise<Score[]> => {
    const saved = await putSavedScore(score);
    return byName([...scores.filter((entry) => entry.id !== score.id), { ...score, ...saved }]);
};

export const deleteScore = async (scores: Score[], id: string): Promise<Score[]> => {
    await deleteSavedScore(id);
    return scores.filter((entry) => entry.id !== id);
};

export const newScoreId = (): string => `score-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const language = (locale: string): 'de' | 'en' => (locale.startsWith('de') ? 'de' : 'en');

/**
 * Where a metric sits in the sidebar: its block (for the icon) and its path — the category, then
 * the group inside it. The same functions as the sidebar decide both, so the completion list shows
 * a metric under the same heading the reader would find it under.
 */
const placeOf = (metric: ScoringMetric, t: TFunction, locale: string): { block: string; path: string[] } => {
    const source = metric.source;
    const lang = language(locale);
    const group = metric.group?.[lang] ?? metric.group?.de;
    if (source.kind === 'zensus') {
        return { block: `theme:${source.theme}`, path: [t(`heatmap.categories.${zensusCategory(source.theme)}`), group ?? source.theme] };
    }
    if (source.kind === 'inkar') {
        return { block: `inkar:${source.type}`, path: [t(`heatmap.categories.${inkarCategory(source.type)}`), group ?? source.type] };
    }
    if (source.kind === 'poi') {
        const type = POI_TYPES[source.className];
        const poiGroup = type?.group ?? 'infrastructure';
        return {
            block: `poi:${poiGroup}`,
            path: [
                t(`heatmap.categories.${poiCategory(poiGroup)}`),
                poiGroupLabel(poiGroup, locale),
                type ? poiTypeLabel(type, locale) : source.className,
            ],
        };
    }
    return {
        block: source.group,
        path: [t(`heatmap.categories.${fixedCategory(source.group)}`), t(`heatmap.${source.group}.label`)],
    };
};

/**
 * The variables a formula may use: every metric of the catalogue, with the path it has in the
 * sidebar. Completion matches name, label and path, because people look for "Miete" or "Schule",
 * not for `durchschn_nettokaltmiete` or `poi.School.abstand`.
 */
export const variablesFromScoringCatalog = (
    metrics: ScoringMetric[],
    t: TFunction,
    locale: string,
    granularity: Granularity = 'cell',
    distribution: Distribution = 'buildings',
): FormulaVariable[] =>
    metrics.map((metric) => {
        const { block, path } = placeOf(metric, t, locale);
        const label = metric.label[language(locale)] ?? metric.label.de;
        const note = aggregationKey(metric, granularity, distribution);
        return {
            name: metric.id,
            label: metric.unit ? `${label} (${metric.unit})` : label,
            hint: metric.id,
            path,
            block,
            note: note ? t(`heatmap.scores.aggregation.${note}`) : null,
        };
    });

/**
 * How a metric reaches the units of the score — the server's rules (scoring/values.ts), named for
 * the reader. Null where the metric sits on the score's own granularity and nothing is converted.
 */
export const aggregationKey = (metric: ScoringMetric, granularity: Granularity, distribution: Distribution): string | null => {
    const { basis, kind } = metric;
    if (basis === granularity) return null;
    if (basis === 'grid1km' || basis === 'region' || basis === 'area') return `${granularity}.${basis}`;
    if (basis === 'point') return `${granularity}.${kind}`;
    const amount = kind === 'extensive' ? 'extensive' : 'intensive';
    if (granularity === 'parcel' && basis === 'cell' && amount === 'extensive') return `parcel.cell.extensive_${distribution}`;
    return `${granularity}.${basis}.${amount}`;
};
